import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { type StaticDecode, Type } from 'typebox';
import * as TypeCompiler from 'typebox/compile';
import { Nullable, ReadonlyNullable } from 'schema/schema';
import { http, httpChecked, queryClient } from 'util/fetch';
import { redirectTo } from 'util/routes';

const transformEpoch = ReadonlyNullable(
  Type.Codec(Type.Number())
    .Decode((value) => new Date(value))
    .Encode((value) => value.getTime()),
);

const schema = Type.Object({
  enabled: Type.Readonly(Type.Boolean()),
  notice: Type.Readonly(
    Type.Codec(Nullable(Type.String()))
      .Decode((value) => value ?? '')
      .Encode((value) => value),
  ),
  updatedAt: transformEpoch,
  updatedBy: ReadonlyNullable(Type.String()),
  companyName: Type.Readonly(Type.String()),
  mustAgree: Type.Readonly(Type.Boolean()),
  agreedAt: transformEpoch,
});

const checker = TypeCompiler.Compile(schema);
export type PrivacyNotice = StaticDecode<typeof schema>;

const url = '/privacy-notice';
const queries = {
  fetch: queryOptions({
    queryKey: ['privacy-notice'] as const,
    queryFn: ({ signal }) => httpChecked.get(url, checker, { signal }),
  }),
};

export function usePrivacyNotice() {
  const { data } = useQuery({
    ...queries.fetch,
    // we cache the response forever, but let's refetch every 10 minutes just in
    // case it changes
    refetchInterval: 60_000 * 10,
    staleTime: Infinity,
  });

  return data;
}

export function useUpdatePrivacyNotice() {
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (body: Pick<PrivacyNotice, 'enabled' | 'notice'>) =>
      http.post(url, { body }),
    onSettled: () => queryClient.invalidateQueries(queries.fetch),
  });

  return mutate;
}

const acceptSchema = Type.Object({ returnTo: Type.Readonly(Type.String()) });
const acceptChecker = TypeCompiler.Compile(acceptSchema);

export function useAcceptPrivacyNotice() {
  const { mutate } = useMutation({
    mutationFn: () =>
      httpChecked.post('/api/v1/privacy-notice-agreement', acceptChecker),
    onSuccess: (data) => redirectTo(data.returnTo),
  });

  return mutate;
}

export async function prefetchPrivacyNotice() {
  await queryClient.prefetchQuery({ ...queries.fetch, staleTime: Infinity });
}
